<script lang="ts" setup>
import Config from '@/components/Config.vue';
import ServerAuth from '@/components/ServerAuth.vue';
import StatusList from '@/components/StatusList.vue';
import type { Account, FetchMode, ShowTimelineEvent } from './types';
</script>

<script lang="ts">
import { nextTick } from 'vue';
import { HttpErrorItem, type Status, type TimelineIdSpec } from './types';
import { buildTimelineUrl } from './utils';

type Config = {
  server: string;
  mode: FetchMode;
  bearerToken: string;
}

type StatusId = string

export default {
  data(): {
    currentConfig: Config | null,
    statuses: Status[] | null,
    hasLast: boolean,
    hasLatest: boolean,
    alert: string | null,
    showAuth: boolean,
    fetchingFirstStatuses: boolean,
    fetchingLaterStatuses: boolean,
    fetchingMoreStatuses: boolean,
    abortController: AbortController
  } {
    return {
      currentConfig: null,
      statuses: null,
      hasLast: false,
      hasLatest: false,
      alert: null,
      showAuth: false,
      fetchingFirstStatuses: false,
      fetchingLaterStatuses: false,
      fetchingMoreStatuses: false,
      abortController: new AbortController()
    }
  },
  methods: {
    setConfig(config: Config | null, fetchingFirstStatuses: boolean): void {
      this.currentConfig = config;
      this.statuses = null;
      this.hasLast = false;
      this.hasLatest = false;
      this.clearAlert();
      this.showAuth = false;
      this.fetchingFirstStatuses = fetchingFirstStatuses;
      this.fetchingLaterStatuses = false;
      this.fetchingMoreStatuses = false;
      this.abortController.abort();
      this.abortController = new AbortController();
    },
    beforeId(id: StatusId | ""): Promise<Status[] | null> {
      const config = this.currentConfig;
      if (!config) {
        throw new Error("currentConfig is null. This is a bug.");
      }

      const idSpec: TimelineIdSpec = id === "" ? { type: 'none' } : { type: 'max', id };
      const url = buildTimelineUrl(config.server, idSpec, config.mode);
      return this.fetchStatuses(url);
    },
    laterId(id: StatusId): Promise<Status[] | null> {
      const config = this.currentConfig;
      if (!config) {
        throw new Error("currentConfig is null. This is a bug.");
      }
      const url = buildTimelineUrl(config.server, { type: 'min', id }, config.mode);
      return this.fetchStatuses(url);
    },
    // 取得したstatusesを返す
    // エラー時、aborted 時はnull
    async fetchStatuses(url: string): Promise<Status[] | null> {
      if (!this.currentConfig) {
        throw new Error("currentConfig is null. This is a bug.");
      }
      this.clearAlert();
      const myAbortController = this.abortController;
      const headers = this.currentConfig.bearerToken ?
        { 'Authorization': `Bearer ${this.currentConfig.bearerToken}` } : undefined;
      return await fetch_json(url, myAbortController, headers).then(statuses => {
        return statuses;
      }).catch(e => {
        const isAborted = this.abortController !== myAbortController;
        if (isAborted) {
          return null;
        }

        // エラー表示を行う
        this.alert = e.message.toString();
        if (e instanceof HttpErrorItem) {
          e.jsonPromise.then(json => this.alert += " : " + json.error)
            .catch(() => { });
          if (e.code == 422) this.showAuth = true;
        }

        return null;
      });
    },
    clearAlert(): void {
      this.alert = null;
    },
    async fetchMore(): Promise<void> {
      if (!this.currentConfig || !this.statuses) {
        throw new Error("currentConfig or statuses is null. This is a bug.");
      }
      const oldest = this.statuses[this.statuses.length - 1];
      if (oldest == null) {
        throw new Error("statuses is empty. This is a bug.");
      }

      this.fetchingMoreStatuses = true;
      const got = await this.beforeId(oldest.id);
      this.fetchingMoreStatuses = false;

      if (!got) return;
      this.statuses = this.statuses.concat(got);
      this.hasLast = got.length === 0;
    },
    async fetchLater(): Promise<void> {
      if (!this.currentConfig || !this.statuses) {
        throw new Error("currentConfig or statuses is null. This is a bug.");
      }
      const latest = this.statuses[0];
      if (latest == null) {
        throw new Error("statuses is empty. This is a bug.");
      }

      this.fetchingLaterStatuses = true;
      const got = await this.laterId(latest.id);
      this.fetchingLaterStatuses = false;

      if (!got) return;
      this.statuses = got.concat(this.statuses);
      this.hasLatest = got.length === 0;

      // スクロール位置補正
      const oldHeight = document.body.clientHeight;
      nextTick(() => {
        const currentHeight = document.body.clientHeight;
        document.documentElement.scrollTop += currentHeight - oldHeight;
      });
    },
    async showTimeline(event: ShowTimelineEvent): Promise<void> {

      const resolveConfig = async (event: ShowTimelineEvent): Promise<Config | null> => {
        const eventType = event.mode.type;

        let mode;
        switch (eventType) {
          case "user":
            // TODO error handling and update loading state
            const userId = await resolveUserId(event.server, event.mode.userAcct);

            if (!userId.found) {
              this.alert = userId.detail;
              return null;
            }

            mode = {
              type: "user",
              userId: userId.userId
            } as const;
            break;
          case "timeline":
            mode = event.mode;
            break;
          default:
            throw new Error(eventType satisfies never);
        }

        return {
          server: event.server,
          mode,
          bearerToken: event.bearerToken
        };
      }

      // clear config
      this.setConfig(null, true);

      const id = event.id;
      let got;

      this.fetchingFirstStatuses = true;
      try {
        const config = await resolveConfig(event);
        if (config == null) {
          return;
        }
        this.setConfig(config, true);
        got = await this.beforeId(id);
      } finally {
        this.fetchingFirstStatuses = false;
      }

      if (!got) return;
      this.statuses = got;

      if (got[0] && Number(got[0].id) < 10e10 && Number(id) >= 10e10)
        this.alert = "時刻指定できませんでした。(時刻指定には、そのときのサーバーがMastodon v2.0以降であることが求められます)";
    },
    setAlert(str: string): void {
      this.alert = str;
    }
  }
}

type ResolveUserResult = {
  found: true;
  userId: string;
} | {
  found: false;
  reason: "failed" | "notFound";
  detail: string;
}

const resolveUserId = (domain: string, userAcct: string): Promise<ResolveUserResult> => {
  return fetch_json(`https://${domain}/api/v1/accounts/lookup?acct=${encodeURIComponent(userAcct)}`, undefined)
    .then((account: Account) => {
      return { found: true, userId: account.id } as const;
    })
    .catch(async e => {
      if (e instanceof HttpErrorItem) {
        const jsonerror = await e.jsonPromise.then(json => json.error as string)
          .catch(() => null);
        if (e.code == 404) {
          return { found: false, reason: "notFound", detail: "アカウントが存在しません" + " : " + jsonerror };
        } else {
          return { found: false, reason: "failed", detail: e.message.toString() + " : " + jsonerror };
        }
      } else {
        return { found: false, reason: "failed", detail: e.message.toString() };
      }
    });
};

/**
 * JSONデータを取得
 * @param url - URL
 * @param abortController - AbortController
 * @param headers - ヘッダー
 * @returns JSONデータ
 */
function fetch_json(url: string, abortController: AbortController | undefined, headers: { [key: string]: string } = {}): Promise<any> {
  return new Promise((resolve, reject) => {
    const rejector = (e: HttpErrorItem) => reject(e);
    fetch(url, {
      headers: headers,
      signal: abortController?.signal
    }).then(res => {
      if (!res.ok) return rejector(new HttpErrorItem(res.status, "http not ok (" + res.status + (res.statusText ? (" " + res.statusText) : '') + ")", res.json()));
      res.json().then(resolve).catch(rejector)
    }).catch(rejector);
  });
}
</script>

<template>
  <p>時間やID指定でLTL/FTLもしくは指定アカウントの過去トゥートを遡れます。<br>
    現在のタイムラインも見られます。</p>
  <main id="app" v-cloak>
    <config :loading="fetchingFirstStatuses" @show-timeline="showTimeline" @alert="setAlert">
    </config>

    <div v-if="alert" v-text="alert" class="toast" :class="[showAuth ? 'toast-warning' : 'toast-error']"></div>
    <server-auth v-if="showAuth"></server-auth>

    <div v-if="statuses && statuses.length">
      <button class="btn" :class="{ loading: fetchingLaterStatuses }" @click="fetchLater()"
        :disabled="fetchingLaterStatuses">より新しいものを見る</button>
      <span class="chip text-dark mx-2">id: {{ statuses[0]!.id }}</span>
      <p v-if="hasLatest">ここが取得時点での最新です。</p>
    </div>
    <status-list v-if="statuses != null" :statuses="statuses"></status-list>
    <div v-if="statuses && statuses.length">
      <p v-if="hasLast">ここが終点です。</p>
      <button v-else class="btn" :class="{ loading: fetchingMoreStatuses }" @click="fetchMore()"
        :disabled="fetchingMoreStatuses">さらに遡る</button>
      <span class="chip text-dark mx-2">id: {{ statuses[statuses.length - 1]!.id }}</span>
    </div>
  </main>
</template>

<style scoped></style>
